import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getBanner, insertBanner } from "@braze/web-sdk";
import type { Banner } from "@braze/web-sdk";
import { removeCachedBannerHeight } from "../helpers/brazeBannerHeightCache";
import {
  canResolveBannersFromSdk,
  getBannerResolutionPhase,
  registerRewardsBrazePlacementListener,
} from "../helpers/rewardsBrazePlacementCoordinator";

const BRAZE_ENABLED = Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING);
const BRAZE_LOGGING_ENABLED = Boolean(
  process.env.REACT_APP_BRAZE_ENABLE_CONSOLE_LOGGING
);

function placementLog(logLabel: string, ...args: unknown[]) {
  if (!BRAZE_LOGGING_ENABLED) return;
  // eslint-disable-next-line no-console
  console.log(`[${logLabel}]`, ...args);
}

/**
 * Shared Braze banner placement hook for rewards page slots (new placements only).
 * Uses one coordinated refresh for all rewards placements so multiple banners can show.
 */
export function useRewardsBrazePlacement(
  placementId: string,
  logLabel: string
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<Banner | null>(null);
  const isHandlingBannerUpdateRef = useRef(false);
  const hasEverShownRef = useRef(false);
  const [showBrazeBanner, setShowBrazeBanner] = useState(false);

  const clearContainer = useCallback(() => {
    bannerRef.current = null;
    if (containerRef.current) {
      containerRef.current.replaceChildren();
    }
  }, []);

  const insertIfReady = useCallback(() => {
    if (!bannerRef.current || !containerRef.current) return;
    insertBanner(bannerRef.current, containerRef.current);
  }, []);

  const applyBannerFromSdk = useCallback(() => {
    if (!canResolveBannersFromSdk()) {
      return false;
    }

    const phase = getBannerResolutionPhase();
    const banner = getBanner(placementId);
    bannerRef.current = banner ?? null;
    const shouldShow = Boolean(banner && !banner.isControl);

    if (shouldShow) {
      hasEverShownRef.current = true;
      setShowBrazeBanner(true);
    } else if (phase === "fresh" || banner?.isControl) {
      hasEverShownRef.current = false;
      setShowBrazeBanner(false);
      if (phase === "fresh") {
        removeCachedBannerHeight(placementId);
      }
    } else if (!hasEverShownRef.current) {
      setShowBrazeBanner(false);
    }

    placementLog(logLabel, "applyBannerFromSdk", {
      placementId,
      phase,
      hasBanner: Boolean(banner),
      isControl: banner?.isControl,
      shouldShow,
    });
    return shouldShow;
  }, [placementId, logLabel]);

  useEffect(() => {
    if (!BRAZE_ENABLED || !placementId) return;

    const handleBannersUpdate = () => {
      if (isHandlingBannerUpdateRef.current) return;
      isHandlingBannerUpdateRef.current = true;
      const shouldShow = applyBannerFromSdk();
      isHandlingBannerUpdateRef.current = false;

      if (shouldShow) {
        requestAnimationFrame(() => {
          requestAnimationFrame(insertIfReady);
        });
        return;
      }

      clearContainer();
    };

    const unregister = registerRewardsBrazePlacementListener(handleBannersUpdate);

    return () => {
      unregister();
    };
  }, [placementId, applyBannerFromSdk, insertIfReady, clearContainer, logLabel]);

  useLayoutEffect(() => {
    if (!showBrazeBanner) return;
    insertIfReady();
  }, [showBrazeBanner, insertIfReady]);

  return {
    containerRef,
    showBrazeBanner: BRAZE_ENABLED && showBrazeBanner,
    placementId,
  };
}
